<h2 mat-dialog-title class="aca-admin-dialog__title">
  <mat-icon>history</mat-icon>
  {{ 'DMS.JOBS.RUN_TITLE' | translate }} — {{ run.jobName }}
</h2>

<div mat-dialog-content class="aca-admin-dialog__body aca-run__body">

  <!-- ── summary grid ─────────────────────────────────────────────────── -->
  <div class="aca-run__grid">
    <span class="aca-run__k">{{ 'DMS.JOBS.COL_TRIGGER' | translate }}</span>
    <span class="aca-run__v">
      <span class="aca-jobs__chip" [class.aca-jobs__chip--manual]="run.triggeredBy?.startsWith('manual')">
        {{ run.triggeredBy?.startsWith('manual') ? ('DMS.JOBS.TRIG_MANUAL' | translate) : ('DMS.JOBS.TRIG_SCHED' | translate) }}
      </span>
      <span class="aca-jobs__dryrun" *ngIf="run.dryRun">{{ 'DMS.JOBS.DRYRUN_BADGE' | translate }}</span>
    </span>

    <span class="aca-run__k">{{ 'DMS.JOBS.COL_ACTION' | translate }}</span>
    <span class="aca-run__v">{{ ('DMS.JOBS.ACT_' + (run.action | uppercase)) | translate }}</span>

    <span class="aca-run__k">{{ 'DMS.JOBS.COL_STARTED' | translate }}</span>
    <span class="aca-run__v">{{ dateLabel(run.startedAt) }}</span>

    <span class="aca-run__k">{{ 'DMS.JOBS.COL_FINISHED' | translate }}</span>
    <span class="aca-run__v">{{ dateLabel(run.finishedAt) }}</span>

    <span class="aca-run__k">{{ 'DMS.JOBS.COL_DURATION' | translate }}</span>
    <span class="aca-run__v">{{ (run.durationMs / 1000) | number: '1.0-1' }} s</span>

    <span class="aca-run__k">{{ 'DMS.JOBS.COL_MATCHED' | translate }}</span>
    <span class="aca-run__v">{{ run.matched }}</span>

    <span class="aca-run__k">{{ 'DMS.JOBS.COL_PROCESSED' | translate }}</span>
    <span class="aca-run__v">{{ run.processed }}</span>

    <span class="aca-run__k" *ngIf="run.emailed?.length">{{ 'DMS.JOBS.COL_EMAILED' | translate }}</span>
    <span class="aca-run__v" *ngIf="run.emailed?.length">{{ run.emailed?.join(', ') }}</span>
  </div>

  <!-- ── the query the run actually used ──────────────────────────────── -->
  <ng-container *ngIf="run.query">
    <h3 class="aca-run__section">{{ 'DMS.JOBS.RUN_QUERY' | translate }}</h3>
    <pre class="aca-run__query">{{ run.query }}</pre>
  </ng-container>

  <!-- ── per-node errors ──────────────────────────────────────────────── -->
  <ng-container *ngIf="run.errors?.length">
    <h3 class="aca-run__section aca-run__section--err">
      <mat-icon>error_outline</mat-icon>
      {{ 'DMS.JOBS.COL_ERRORS' | translate }} ({{ run.errors.length }})
    </h3>
    <ul class="aca-run__list">
      <li *ngFor="let e of run.errors" class="aca-run__err">
        <span class="aca-run__errname" [matTooltip]="e.nodeId">{{ e.name || e.nodeId }}</span>
        <span class="aca-run__errmsg">{{ e.message }}</span>
      </li>
    </ul>
  </ng-container>

  <!-- ── every file the run touched: name, location, size, outcome ────── -->
  <ng-container *ngIf="run.items?.length">
    <h3 class="aca-run__section">{{ 'DMS.JOBS.RUN_FILES' | translate }} ({{ run.items?.length }})</h3>
    <p class="aca-run__capped" *ngIf="itemsCapped">
      {{ 'DMS.JOBS.RUN_FILES_CAPPED' | translate: { shown: run.items?.length, matched: run.matched } }}
    </p>
    <div class="aca-run__filewrap">
      <table class="aca-run__files">
        <thead>
          <tr>
            <th>{{ 'DMS.JOBS.F_NAME' | translate }}</th>
            <th>{{ 'DMS.JOBS.RUN_LOCATION' | translate }}</th>
            <th>{{ 'DMS.JOBS.COL_SIZE' | translate }}</th>
            <th>{{ 'DMS.JOBS.F_MODIFIED' | translate }}</th>
            <th>{{ 'DMS.JOBS.RUN_STATUS' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of run.items" [class.aca-run__filerow--bad]="item.status !== 'processed'">
            <td class="aca-run__fname" [matTooltip]="item.name">
              <mat-icon>description</mat-icon>{{ item.name }}
            </td>
            <td class="aca-run__fpath" [matTooltip]="item.path">{{ item.path || '—' }}</td>
            <td class="aca-run__fsize">{{ sizeLabel(item.size) }}</td>
            <td class="aca-run__fdate">{{ dateLabel(item.modified) }}</td>
            <td>
              <span
                class="aca-run__status"
                [class.aca-run__status--ok]="item.status === 'processed'"
                [class.aca-run__status--skip]="item.status === 'skipped'"
                [class.aca-run__status--fail]="item.status === 'failed'"
                [matTooltip]="item.error || ''"
              >
                <mat-icon>{{ statusIcon(item) }}</mat-icon>{{ statusLabel(item) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </ng-container>

  <!-- ── sample names — shown only for old reports that predate items[] ── -->
  <ng-container *ngIf="!run.items?.length && run.sample?.length">
    <h3 class="aca-run__section">{{ 'DMS.JOBS.RUN_SAMPLE' | translate }} ({{ run.sample?.length }})</h3>
    <ul class="aca-run__list">
      <li *ngFor="let name of run.sample" class="aca-run__item">
        <mat-icon>description</mat-icon>{{ name }}
      </li>
    </ul>
  </ng-container>
</div>

<div mat-dialog-actions class="aca-admin-dialog__actions">
  <button mat-stroked-button type="button" class="aca-run__export" (click)="exportXlsx()">
    <mat-icon>download</mat-icon>{{ 'DMS.JOBS.EXPORT_EXCEL' | translate }}
  </button>
  <span class="aca-run__flex"></span>
  <button mat-flat-button color="primary" (click)="close()">{{ 'DMS.ASSISTANT.CLOSE' | translate }}</button>
</div>
